import {ApiFeature, ProductName, ProductType} from 'pikaparam';
import {TemperatureUnit} from 'puffco-api-axios-client';

import {Constants} from '../../../../constants';
import {
  ChamberType,
  DeviceSettings,
  LightingPattern,
  MoodLight,
  OperatingState,
} from '../../../types';
import {ConnectionMetadata} from '../ConnectionMetadata';
import {StartPeakLogCollectorOptions} from './PeakLogCollector';
import {IUnitCycleAccess} from './UnitCycleAccess';
import {IUnitLedApi} from './UnitLedApi';

export interface DeviceAttributes extends DabSummary {
  boostTemperature: number;
  boostTime: number;
  devBrightness: Buffer;
  mode: number;
  dateOfBirth: number;
  utcTime: number;
  lanternTime: number;
  selectedHeatCycle: number;
  stealth: number;
  batteryLevel: number;
  batteryCapacity: number;
  chargeState: number;
  chargeEstimatedTimeToFull: number;
  dabTotalTime: number;
  chamberType: ChamberType;
  currentTemperature: number;
  targetTemperature: number;
  stateElapsedTime: number;
  stateTotalTime: number;
  operatingState: OperatingState;
  faultEndIndex: number;
}

export enum FirmwareType {
  Flat = 'Flat',
  Lorax = 'Lorax',
}

export type PeakFeature = keyof (typeof Constants)['MINIMUM_FIRMWARE_VERSION'];

export interface Broadcast {
  counter: number;
  key: number[];
}

export interface DabSummary {
  totalHeatCycles: number;
  dabsPerDay: number;
  approxDabsRemaining: number;
}

export interface IPeakProduct {
  name?: ProductName;
  type?: ProductType;
  consistent: boolean;
  features?: ApiFeature[];
}

export type ColorAndPattern = {
  color: string;
  pattern?: LightingPattern;
};

export type WriteLanternOptions = ColorAndPattern | MoodLight;

export const isMoodLight = (m: ColorAndPattern | MoodLight): m is MoodLight =>
  !!(m as MoodLight).category;

export interface IPeakDevice {
  product: IPeakProduct;
  firmwareType: FirmwareType;
  peripheralId: string;
  name: string;
  leds: IUnitLedApi;
  cycles: IUnitCycleAccess;
  softwareRevision?: string;
  gitHash?: string;
  serialNumber?: string;
  modelNumber?: string;
  euid?: string;
  broadcast?: Broadcast;
  attributes: Partial<DeviceAttributes>;
  connected: boolean;
  metadata?: ConnectionMetadata;

  initialize(): Promise<void>;

  supports(feature: PeakFeature): boolean;

  updateBatterySaver(enabled: boolean): Promise<void>;

  readDabSummary(): Promise<DabSummary>;
  readDeviceBattery(): Promise<number>;
  readDeviceAttributes(): Promise<DeviceAttributes>;
  readStealthMode(): Promise<number>;
  readCurrentTemperature(): Promise<number>;
  readTotalHeatCycles(): Promise<number>;
  readDabbingValues(): Promise<{
    state: OperatingState;
    settings: DeviceSettings;
  }>;
  writeDeviceName(name: string): Promise<void>;
  writeDeviceReadyProfile(index: number): Promise<void>;
  writeReadyModeOff(): Promise<void>;
  writeDeviceBrightness(brightness: number): Promise<void>;
  writeStealthMode(mode: number): Promise<void>;
  writeMasterOff(): Promise<void>;
  writeUtcTime(newTime: number): Promise<void>;
  writeResetDevice(): Promise<void>;

  startDabbing(index: number): Promise<void>;
  stopDabbing(): Promise<void>;
  addDabbingTime(time: number): Promise<void>;
  addDabbingTemperature(
    temperature: number,
    unit: TemperatureUnit,
  ): Promise<void>;

  startLantern(options?: {time: number}): Promise<void>;
  stopLantern(): Promise<void>;

  writeLantern(options: WriteLanternOptions): Promise<void>;

  writeBoostTemperature(temperature: number): Promise<void>;
  writeBoostDuration(temperature: number): Promise<void>;

  subscribeToLogs(options: StartPeakLogCollectorOptions): void;
  watch(): Promise<void>;

  disconnect(): Promise<void>;
}
